import { useMemo, useState } from "react";
import { AlertTriangle, Upload } from "lucide-react";
import { Modal } from "../ui/Modal";
import { ScrollShadow } from "../ui/ScrollShadow";
import {
  dataSheetsApi,
  type DataSheetColumn,
  type DataSheetRecord,
  type LookupsResponse,
} from "../../api/dataSheetsApi";
import {
  findExistingByKeys,
  isReferenceType,
  lookupListFor,
  parseFile,
  resolveCell,
  suggestMapping,
  type ImportMode,
  type ParsedFile,
  type RefMode,
  type ResolvedCell,
} from "../../lib/dataSheetTransfer";

type Step = "file" | "map" | "mode" | "preview" | "running" | "done";

const PREVIEW_LIMIT = 200;

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "0.9rem",
  fontFamily: "inherit",
  background: "white",
};

/**
 * Import wizard for a data sheet (CSV / Excel):
 *  1. file    — pick + parse, quick peek at the first rows
 *  2. map     — file column → sheet slot; Value/Reference per reference column
 *  3. mode    — append / replace all / update existing on 1+ key columns
 *  4. preview — every cell resolved & validated; unresolved cells must be
 *               fixed inline (import stays blocked until all are ok)
 *  5. run     — sequential create/update/delete calls with progress
 */
export function DataSheetImportModal({
  open,
  onClose,
  sheetId,
  columns,
  records,
  lookups,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  sheetId: number;
  columns: DataSheetColumn[];
  records: DataSheetRecord[];
  lookups: LookupsResponse;
  onImported: () => void;
}) {
  const [step, setStep] = useState<Step>("file");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<(number | null)[]>([]);
  const [refModes, setRefModes] = useState<Record<number, RefMode>>({});
  const [importMode, setImportMode] = useState<ImportMode>("append");
  const [keySlots, setKeySlots] = useState<number[]>([]);
  const [confirmReplace, setConfirmReplace] = useState(false);
  /** Inline fixes made in the preview, keyed `${rowIndex}:${slot}`. */
  const [overrides, setOverrides] = useState<Record<string, ResolvedCell>>({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [runErrors, setRunErrors] = useState<string[]>([]);
  const [summary, setSummary] = useState({ created: 0, updated: 0, deleted: 0 });

  const columnBySlot = useMemo(() => new Map(columns.map((c) => [c.slot, c])), [columns]);

  /** Slots that received a file column, in slot order. */
  const mappedSlots = useMemo(
    () => columns.map((c) => c.slot).filter((s) => mapping.includes(s)),
    [columns, mapping],
  );

  /** Resolved matrix: one 10-slot value array + per-slot cell per file row. */
  const resolved = useMemo(() => {
    if (!parsed) return [];
    return parsed.rows.map((row, ri) => {
      const cells = new Map<number, ResolvedCell>();
      const values: (string | null)[] = Array.from({ length: 10 }, () => null);
      mapping.forEach((slot, fi) => {
        if (slot == null) return;
        const col = columnBySlot.get(slot);
        if (!col) return;
        const cell =
          overrides[`${ri}:${slot}`] ??
          resolveCell(col, refModes[slot] ?? "value", row[fi] ?? "", lookups);
        cells.set(slot, cell);
        values[slot - 1] = cell.value;
      });
      return { cells, values };
    });
  }, [parsed, mapping, refModes, overrides, columnBySlot, lookups]);

  const unresolvedCount = useMemo(
    () => resolved.reduce((n, r) => n + [...r.cells.values()].filter((c) => c.status === "unresolved").length, 0),
    [resolved],
  );

  const reset = () => {
    setStep("file");
    setParsed(null);
    setParseError(null);
    setMapping([]);
    setRefModes({});
    setImportMode("append");
    setKeySlots([]);
    setConfirmReplace(false);
    setOverrides({});
    setProgress({ done: 0, total: 0 });
    setRunErrors([]);
    setSummary({ created: 0, updated: 0, deleted: 0 });
  };

  const close = () => {
    if (step === "running") return;
    reset();
    onClose();
  };

  /** Acknowledge the result: close the dialog and let the page refresh.
   *  Refreshing earlier would unmount this modal mid-"done" (the page
   *  hides it while loading) and bring it back at step 1. */
  const finish = () => {
    reset();
    onImported();
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    setParseError(null);
    try {
      const p = await parseFile(file);
      if (p.headers.length === 0) {
        setParseError("The file has no header row.");
        return;
      }
      setParsed(p);
      setMapping(suggestMapping(p.headers, columns));
      setOverrides({});
    } catch (err) {
      setParseError(err instanceof Error ? err.message : "Could not read the file.");
    }
  };

  const toggleKey = (slot: number) =>
    setKeySlots((prev) => (prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]));

  const canProceedFromMode =
    importMode === "append" ||
    (importMode === "replace" && confirmReplace) ||
    (importMode === "upsert" && keySlots.length > 0);

  const run = async () => {
    if (!parsed || unresolvedCount > 0) return;
    setStep("running");
    setRunErrors([]);
    const errors: string[] = [];
    let created = 0;
    let updated = 0;
    let deleted = 0;

    const total = resolved.length + (importMode === "replace" ? records.length : 0);
    setProgress({ done: 0, total });
    let done = 0;
    const tick = () => setProgress({ done: ++done, total });

    // Working copy so upsert also matches rows created earlier in this run.
    let working: DataSheetRecord[] = [...records];

    try {
      if (importMode === "replace") {
        for (const r of records) {
          try {
            await dataSheetsApi.removeRecord(sheetId, r.dataId);
            deleted++;
          } catch (err) {
            errors.push(`Delete #${r.dataId}: ${err instanceof Error ? err.message : "failed"}`);
          }
          tick();
        }
        working = [];
      }

      for (let i = 0; i < resolved.length; i++) {
        const { values } = resolved[i];
        try {
          const existing = importMode === "upsert" ? findExistingByKeys(working, keySlots, values) : undefined;
          if (existing) {
            await dataSheetsApi.updateRecord(sheetId, existing.dataId, { values });
            working = working.map((r) => (r.dataId === existing.dataId ? { ...r, values } : r));
            updated++;
          } else {
            const { dataId } = await dataSheetsApi.createRecord(sheetId, { values });
            working = [...working, { dataId, values, displays: values }];
            created++;
          }
        } catch (err) {
          errors.push(`Row ${i + 2}: ${err instanceof Error ? err.message : "failed"}`);
        }
        tick();
      }
    } finally {
      setSummary({ created, updated, deleted });
      setRunErrors(errors);
      setStep("done");
    }
  };

  const stepTitle: Record<Step, string> = {
    file: "Import — choose a file",
    map: "Import — map columns",
    mode: "Import — what to do with existing rows",
    preview: "Import — check & fix",
    running: "Importing…",
    done: "Import finished",
  };

  return (
    <Modal open={open} onClose={close} title={stepTitle[step]} busy={step === "running"} maxWidth={980} footer={renderFooter()}>
      {step === "file" && renderFile()}
      {step === "map" && renderMap()}
      {step === "mode" && renderMode()}
      {step === "preview" && renderPreview()}
      {step === "running" && renderRunning()}
      {step === "done" && renderDone()}
    </Modal>
  );

  // ---- footer -------------------------------------------------------------

  function renderFooter() {
    const back = (to: Step) => (
      <button type="button" className="btn btn-secondary" onClick={() => setStep(to)}>
        Back
      </button>
    );
    const cancel = (
      <button type="button" className="btn btn-secondary" onClick={close}>
        Cancel
      </button>
    );
    switch (step) {
      case "file":
        return (
          <>
            {cancel}
            <button type="button" className="btn btn-primary" onClick={() => setStep("map")} disabled={!parsed}>
              Next
            </button>
          </>
        );
      case "map":
        return (
          <>
            {back("file")}
            <button type="button" className="btn btn-primary" onClick={() => setStep("mode")} disabled={mappedSlots.length === 0}>
              Next
            </button>
          </>
        );
      case "mode":
        return (
          <>
            {back("map")}
            <button type="button" className="btn btn-primary" onClick={() => setStep("preview")} disabled={!canProceedFromMode}>
              Next
            </button>
          </>
        );
      case "preview":
        return (
          <>
            {back("mode")}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void run()}
              disabled={unresolvedCount > 0 || resolved.length === 0}
              title={unresolvedCount > 0 ? "Fix the highlighted cells first" : undefined}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Upload size={16} /> Import {resolved.length} {resolved.length === 1 ? "row" : "rows"}
            </button>
          </>
        );
      case "running":
        return null;
      case "done":
        return (
          <button type="button" className="btn btn-primary" onClick={finish}>
            Close
          </button>
        );
    }
  }

  // ---- steps --------------------------------------------------------------

  function renderFile() {
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--portal-muted)" }}>
          Pick a CSV or Excel file. The first row must contain column headers.
        </p>
        <input
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
        />
        {parseError && <Alert>{parseError}</Alert>}
        {parsed && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
              <strong>{parsed.fileName}</strong> · {parsed.rows.length} data {parsed.rows.length === 1 ? "row" : "rows"} · {parsed.headers.length} columns
            </div>
            <PreviewTable headers={parsed.headers} rows={parsed.rows.slice(0, 5)} />
          </div>
        )}
      </div>
    );
  }

  function renderMap() {
    if (!parsed) return null;
    const usedElsewhere = (fi: number, slot: number) => mapping.some((s, i) => i !== fi && s === slot);
    return (
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--portal-muted)" }}>
          Match each file column to a sheet column, or skip it. For reference columns choose whether the
          file holds the <strong>value</strong> (name) or the <strong>reference</strong> (id).
        </p>
        <div style={{ display: "grid", gap: 6 }}>
          {parsed.headers.map((h, fi) => {
            const slot = mapping[fi];
            const col = slot != null ? columnBySlot.get(slot) : undefined;
            return (
              <div
                key={fi}
                style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1fr) minmax(200px, 1fr) auto", gap: 12, alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--portal-border)" }}
              >
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={h}>
                  {h || <em style={{ color: "var(--portal-muted)" }}>(column {fi + 1})</em>}
                  <div style={{ fontWeight: 400, fontSize: "0.75rem", color: "var(--portal-muted)" }}>
                    e.g. {parsed.rows[0]?.[fi] || "—"}
                  </div>
                </div>
                <select
                  className="portal-input-inline"
                  style={inputStyle}
                  value={slot ?? ""}
                  onChange={(e) => {
                    const next = [...mapping];
                    next[fi] = e.target.value === "" ? null : Number(e.target.value);
                    setMapping(next);
                    setOverrides({});
                  }}
                >
                  <option value="">— skip —</option>
                  {columns.map((c) => (
                    <option key={c.slot} value={c.slot} disabled={usedElsewhere(fi, c.slot)}>
                      {c.name} (col{c.slot})
                    </option>
                  ))}
                </select>
                <div style={{ minWidth: 190 }}>
                  {col && isReferenceType(col.typeId) ? (
                    <div style={{ display: "flex", gap: 10, fontSize: "0.85rem" }}>
                      {(["value", "reference"] as RefMode[]).map((m) => (
                        <label key={m} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <input
                            type="radio"
                            name={`import-ref-${col.slot}`}
                            checked={(refModes[col.slot] ?? "value") === m}
                            onChange={() => {
                              setRefModes({ ...refModes, [col.slot]: m });
                              setOverrides({});
                            }}
                          />
                          {m === "value" ? "Value" : "Reference"}
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {mappedSlots.length === 0 && <Alert>Map at least one column to continue.</Alert>}
      </div>
    );
  }

  function renderMode() {
    const option = (m: ImportMode, label: string, help: string) => (
      <label style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, alignItems: "start", padding: "10px 0", borderTop: "1px solid var(--portal-border)" }}>
        <input type="radio" name="import-mode" checked={importMode === m} onChange={() => setImportMode(m)} style={{ marginTop: 4 }} />
        <span>
          <span style={{ fontWeight: 600 }}>{label}</span>
          <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>{help}</div>
        </span>
      </label>
    );
    return (
      <div style={{ display: "grid", gap: 4 }}>
        {option("append", "Add rows", "Imported rows are added; existing rows are left untouched.")}
        {option("replace", "Replace all", `Delete all ${records.length} existing rows first, then import.`)}
        {option("upsert", "Update existing, add the rest", "Match on one or more key columns: a matching row is updated, otherwise a new row is added.")}

        {importMode === "replace" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 8, color: "#c0392b" }}>
            <input type="checkbox" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} />
            I understand this permanently deletes the {records.length} existing rows.
          </label>
        )}

        {importMode === "upsert" && (
          <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
            <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>Key columns (in order)</span>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
              Tick the mapped columns that identify a row. Keys are numbered in the order you pick them.
            </p>
            {mappedSlots.map((slot) => {
              const col = columnBySlot.get(slot);
              const idx = keySlots.indexOf(slot);
              return (
                <label key={slot} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  <input type="checkbox" checked={idx >= 0} onChange={() => toggleKey(slot)} />
                  <span
                    style={{
                      display: "inline-grid",
                      placeItems: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      background: idx >= 0 ? "var(--portal-primary)" : "var(--portal-surface)",
                      color: idx >= 0 ? "#111" : "var(--portal-muted)",
                      border: "1px solid var(--portal-border)",
                    }}
                  >
                    {idx >= 0 ? idx + 1 : "·"}
                  </span>
                  {col?.name}
                </label>
              );
            })}
            {keySlots.length === 0 && <Alert>Pick at least one key column.</Alert>}
          </div>
        )}
      </div>
    );
  }

  function renderPreview() {
    if (!parsed) return null;
    const shown = resolved.slice(0, PREVIEW_LIMIT);
    return (
      <div style={{ display: "grid", gap: 10 }}>
        {unresolvedCount > 0 ? (
          <Alert>
            {unresolvedCount} {unresolvedCount === 1 ? "cell" : "cells"} could not be resolved. Fix them below — import stays disabled until everything checks out.
          </Alert>
        ) : (
          <div style={{ fontSize: "0.9rem", color: "var(--portal-status-success-darker, #2f855a)", fontWeight: 600 }}>
            All {resolved.length} rows resolved. Ready to import
            {importMode === "replace" ? ` (replacing ${records.length} existing rows)` : importMode === "upsert" ? ` (updating on ${keySlots.length} key ${keySlots.length === 1 ? "column" : "columns"})` : ""}.
          </div>
        )}
        <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: "50vh", overflowY: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                {mappedSlots.map((slot) => (
                  <th key={slot} style={thStyle}>
                    {columnBySlot.get(slot)?.name}
                    {keySlots.includes(slot) && (
                      <span style={{ marginLeft: 6, fontSize: "0.7rem", color: "var(--portal-muted)" }}>key {keySlots.indexOf(slot) + 1}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, ri) => (
                <tr key={ri}>
                  <td style={{ ...tdStyle, color: "var(--portal-muted)" }}>{ri + 2}</td>
                  {mappedSlots.map((slot) => {
                    const col = columnBySlot.get(slot)!;
                    const cell = r.cells.get(slot)!;
                    return (
                      <td key={slot} style={{ ...tdStyle, background: cell.status === "unresolved" ? "#fdecea" : undefined }}>
                        {cell.status === "unresolved" ? (
                          <UnresolvedEditor
                            column={col}
                            cell={cell}
                            lookups={lookups}
                            onFix={(fixed) => setOverrides({ ...overrides, [`${ri}:${slot}`]: fixed })}
                          />
                        ) : cell.status === "empty" ? (
                          <span style={{ color: "var(--portal-muted)", fontStyle: "italic" }}>—</span>
                        ) : (
                          <span title={cell.raw !== cell.display ? `from "${cell.raw}"` : undefined}>{cell.display}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollShadow>
        {resolved.length > PREVIEW_LIMIT && (
          <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>
            Showing the first {PREVIEW_LIMIT} of {resolved.length} rows; all rows are validated.
          </div>
        )}
      </div>
    );
  }

  function renderRunning() {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.9rem" }}>
          {progress.done} of {progress.total} operations…
        </div>
        <div style={{ height: 8, background: "var(--portal-surface)", borderRadius: 999, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: "var(--portal-primary)", transition: "width 0.15s" }} />
        </div>
      </div>
    );
  }

  function renderDone() {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ fontSize: "0.95rem" }}>
          <strong>{summary.created}</strong> added · <strong>{summary.updated}</strong> updated · <strong>{summary.deleted}</strong> deleted
        </div>
        {runErrors.length > 0 && (
          <Alert>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{runErrors.length} {runErrors.length === 1 ? "row" : "rows"} failed:</div>
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: "auto" }}>
              {runErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}
      </div>
    );
  }
}

// ---- small parts ---------------------------------------------------------

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--portal-border)",
  color: "var(--portal-muted)",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  position: "sticky",
  top: 0,
  background: "white",
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "6px 10px",
  borderBottom: "1px solid var(--portal-border)",
  verticalAlign: "top",
  maxWidth: 240,
};

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>{children}</div>
    </div>
  );
}

function PreviewTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={thStyle}>{h || `(column ${i + 1})`}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>{headers.map((_, ci) => <td key={ci} style={tdStyle}>{r[ci]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </ScrollShadow>
  );
}

/** Inline fixer for an unresolved cell: a lookup picker for reference
 *  columns, a text box (re-validated on change) for scalars. */
function UnresolvedEditor({
  column,
  cell,
  lookups,
  onFix,
}: {
  column: DataSheetColumn;
  cell: ResolvedCell;
  lookups: LookupsResponse;
  onFix: (fixed: ResolvedCell) => void;
}) {
  if (isReferenceType(column.typeId)) {
    const list = lookupListFor(column.typeId, lookups);
    return (
      <div style={{ display: "grid", gap: 4 }}>
        <span style={{ color: "#c0392b", fontSize: "0.75rem" }}>Not found: "{cell.raw}"</span>
        <select
          className="portal-input-inline"
          style={{ ...inputStyle, padding: "6px 8px", fontSize: "0.85rem" }}
          defaultValue=""
          onChange={(e) => {
            const hit = list.find((i) => String(i.id) === e.target.value);
            if (hit) onFix({ raw: cell.raw, value: String(hit.id), display: hit.name, status: "ok" });
          }}
        >
          <option value="">Pick…</option>
          {list.map((i) => (
            <option key={i.id} value={i.id}>{i.name}</option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <span style={{ color: "#c0392b", fontSize: "0.75rem" }}>Invalid: "{cell.raw}"</span>
      <input
        className="portal-input-inline"
        style={{ ...inputStyle, padding: "6px 8px", fontSize: "0.85rem" }}
        defaultValue={cell.raw}
        placeholder={column.typeId === 8 ? "YYYY-MM-DD" : column.typeId === 9 ? "HH:mm" : "value"}
        onBlur={(e) => {
          const next = resolveCell(column, "value", e.target.value, lookups);
          if (next.status === "ok") onFix(next);
        }}
      />
    </div>
  );
}
